import { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Card, FormGroup } from "@cofano/cck";
import { Box, CircularProgress, Grid2 as Grid, Stack, Alert } from "@mui/material";
import { Edit } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";

import Language from "components/utils/Language";
import { useConfirm } from "components/hooks/useConfirm";

import { FormattedDate } from "utils/DateUtils";
import { LocationLink } from "utils/LinkUtils";

import CallCreateEditDialog from "view/calls/components/dialogs/CallCreateEditDialog";
import { PushingTugCall, PushingTugVoyage } from "view/pushingtug/utils/PushingTugPlanningUtils";

import { DeleteCallDocument } from "__generated__/view/calls/graphql/call.mutations.generated";

interface PushingTugVoyageCallsProps {
	voyage: PushingTugVoyage | null;
	loading: boolean;
	refetch: () => void;
}

const PushingTugVoyageCalls = ({ voyage, loading, refetch }: PushingTugVoyageCallsProps) => {

	const [ call, setCall ] = useState<PushingTugCall | null>(null);

	const [ deleteCall, { loading: deleteCallLoading } ] = useMutation(DeleteCallDocument);

	const confirm = useConfirm();

	return (loading || deleteCallLoading) ? (
		<Box display="flex" justifyContent="center" width="100%">
			<CircularProgress size={24} />
		</Box>
	) : voyage ? (
		<>
			<Stack spacing={2}>
				{voyage.calls.map(call => (
					<CallDetails
						key={call.id}
						call={call}
						onEdit={setCall}
						onDelete={(id) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteCall({
									variables: {
										id,
									},
								}).then(() => {
									refetch();
								});
							});
						}}
					/>
				))}
			</Stack>

			{!!call && (
				<CallCreateEditDialog
					call={call}
					open={true}
					voyageId={voyage.id}
					refetch={refetch}
					onClose={() => setCall(null)}
				/>
			)}
		</>
	) : (
		<Alert severity="warning">
			{Language("NO_VOYAGE_SELECTED")}
		</Alert>
	);
};

interface CallDetailsProps {
	call: PushingTugCall;
	onDelete: (callId: number) => void;
	onEdit: (call: PushingTugCall) => void;
}

const CallDetails = ({ call, onDelete, onEdit }: CallDetailsProps) => (
	<Card
		title={<LocationLink location={call.location} />}
		actions={[ {
			icon: <Edit />,
			onClick: () => onEdit(call),
		}, {
			icon: <Delete />,
			onClick: () => onDelete(call.id),
			disabled: call.linkedChildCalls.length > 0,
		} ]}>
		<Grid container rowSpacing={1} columnSpacing={3}>
			<Grid size={6}>
				<FormGroup label={intl.formatMessage({ id: "CALLS_PTA" })}>
					<FormattedDate date={call.pta} />
				</FormGroup>
			</Grid>

			<Grid size={6}>
				<FormGroup label={intl.formatMessage({ id: "CALLS_PTD" })}>
					<FormattedDate date={call.ptd} />
				</FormGroup>
			</Grid>

			<Grid size={6}>
				<FormGroup label={intl.formatMessage({ id: "AMOUNT_LINKED_CALLS" })}>
					{call.linkedChildCalls.length}
				</FormGroup>
			</Grid>

			<Grid size={6}>
				<FormGroup label={intl.formatMessage({ id: "TOTAL_WEIGHT_LINKED_CALLS" })}>
					{call.linkedChildCalls.reduce((acc, call) => acc + (call.weight ?? 0), 0)}
				</FormGroup>
			</Grid>
		</Grid>
	</Card>
);

export default PushingTugVoyageCalls;
